import React, { createContext } from 'react';
import * as Sentry from '@sentry/react';
import {
  REFRESH_TOKEN_CHECK_INTERVAL,
  REFRESH_TOKEN_MIN_VALIDITY,
} from './constants';
import type { AuthProviderProps, AuthProviderState, UserRole } from './types';
import { checkIfRoleIsIncluded } from './checkRole';
import env from '../util/env';
import { store } from '../createStore';
import { setOffline, setOnline } from '../store/connection/actions';
import keycloak from './keycloak';
import {
  startUserSession,
  enterOfflineSession,
  endUserSession,
} from './userIdentity';
import { checkNetworkConnectivity } from 'util/networkConnectivity';
import { refreshSessionToken } from './tokenRecovery';

interface AuthContextProps {
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  isInitialized: boolean;
  checkIfRoleIsIncluded: (role: UserRole) => boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  login: () => {},
  logout: () => {},
  isAuthenticated: false,
  isInitialized: false,
  checkIfRoleIsIncluded: () => false,
});

const NETWORK_CHECK_TIMEOUT = 5000;
const KEYCLOAK_INIT_TIMEOUT = 10000;

class AuthProvider extends React.Component<
  AuthProviderProps,
  AuthProviderState
> {
  private refreshTokenInterval?: number;
  private unsubscribeConnection?: () => void;
  private wasOnline = store.getState().connectionState.isOnline;
  private isOfflineMode = false;
  private isInitializing = false;

  constructor(props: AuthProviderProps) {
    super(props);
    this.state = { isInitialized: false };
  }

  async componentDidMount() {
    this.unsubscribeConnection = store.subscribe(this.handleConnectionChange);
    try {
      await this.init();
    } catch (error) {
      Sentry.captureException(error, { tags: { auth: 'initialInit' } });
      this.initializeOffline();
    }
    window.addEventListener('focus', this.handleForeground);
    document.addEventListener('visibilitychange', this.handleForeground);
    this.refreshTokenInterval = window.setInterval(
      this.refreshToken,
      REFRESH_TOKEN_CHECK_INTERVAL * 1000,
    );
  }

  componentWillUnmount() {
    this.unsubscribeConnection?.();
    clearInterval(this.refreshTokenInterval);
    window.removeEventListener('focus', this.handleForeground);
    document.removeEventListener('visibilitychange', this.handleForeground);
  }

  private initializeOffline() {
    enterOfflineSession();
    this.isOfflineMode = true;
    store.dispatch(setOffline());
    this.setState({ isInitialized: true });
  }

  private handleConnectionRestored = async () => {
    if (!this.isOfflineMode || this.isInitializing) {
      return;
    }

    store.dispatch(setOffline());

    try {
      await this.init();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { auth: 'connectionRestored' },
      });
    }
  };

  private handleConnectionChange = () => {
    const isOnline = store.getState().connectionState.isOnline;
    const becameOnline = !this.wasOnline && isOnline;
    this.wasOnline = isOnline;

    if (becameOnline) {
      void this.handleConnectionRestored();
    }
  };

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Operation timed out')), timeoutMs),
      ),
    ]);
  }

  async init() {
    if (this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    const isConnected = await checkNetworkConnectivity({
      timeoutMs: NETWORK_CHECK_TIMEOUT,
    });
    if (!isConnected) {
      this.initializeOffline();
      this.isInitializing = false;
      return;
    }

    try {
      const { default: Keycloak } = await import('keycloak-js');

      const keycloakInstance = new Keycloak({
        clientId: 'plant-designer-web',
        realm: env.KEYCLOAK_REALM,
        url: env.KEYCLOAK_URL,
      });

      await this.withTimeout(
        keycloakInstance.init({
          checkLoginIframe: false,
          onLoad: 'check-sso',
        }),
        KEYCLOAK_INIT_TIMEOUT,
      );

      startUserSession(keycloakInstance);
      this.isOfflineMode = false;
      store.dispatch(setOnline());

      if (!keycloak.authenticated) {
        void keycloak.login();
        return;
      }

      try {
        await refreshSessionToken(REFRESH_TOKEN_MIN_VALIDITY);
      } catch {
        // Render the recovery prompt while keeping the current document open.
      }

      const tokenParsed = keycloak.tokenParsed as
        | { sub?: string; email?: string; preferred_username?: string }
        | null
        | undefined;
      const currentUserId = tokenParsed?.sub;

      Sentry.setUser(
        currentUserId
          ? {
              id: currentUserId,
              email: tokenParsed?.email,
              username: tokenParsed?.preferred_username,
            }
          : null,
      );

      this.setState({ isInitialized: true });
    } finally {
      this.isInitializing = false;
    }
  }

  login = () => {
    if (!this.state.isInitialized) {
      throw new Error(
        'Unable to login as keycloak is not inited just yet. Use isInitialized to delay the call.',
      );
    }
    if (keycloak.authenticated) {
      return;
    }
    void keycloak.login();
  };

  logout = async () => {
    endUserSession();
    Sentry.setUser(null);
    await keycloak.logout();
  };

  private handleForeground = () => {
    if (document.visibilityState === 'visible') void this.refreshToken();
  };

  refreshToken = async () => {
    if (!keycloak.authenticated) return;
    try {
      await refreshSessionToken();
    } catch {
      // Recovery reports the failure and prompts without navigating away.
    }
  };

  /**
   * Checks if the user has a certain role
   * @param role Role assigned by keycloak token
   * @returns true if role is included, else false
   */
  checkIfRoleIsIncluded = (role: UserRole) => {
    return checkIfRoleIsIncluded(role);
  };

  render() {
    const { children } = this.props;
    const { isInitialized } = this.state;
    return (
      <AuthContext.Provider
        value={{
          login: this.login,
          logout: this.logout,
          isAuthenticated: keycloak.authenticated,
          isInitialized: isInitialized,
          checkIfRoleIsIncluded: this.checkIfRoleIsIncluded,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }
}

export default AuthProvider;
